import {Router} from '@angular/router';
import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgRedux, Select, Select$} from '@angular-redux3/store';
import {Observable} from 'rxjs';

import {ABlacklist, ADislike, ALike, ANext, APause, APlay, ASlideVolume, AUnblacklist, PlaylistRemoteAppState} from '../../store';
import {AssoMediaEvent, Media} from '../../store/entities';
import {PlayerService} from './player.service';
import {extractPropertyFromArray, isItemInArray, toArray} from '../../utils';
import {environment} from '../../../environments/environment';
import {SyncDialogService} from '../../services/sync-dialog.service';
import {PlaybackService} from '../../services/playback.service';
import {VolumeDialogComponent} from '../../components/dialogs/volume-dialog/volume-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SonosComponent} from './sonos/sonos.component';
import {AGetAudioDevices} from '../../store/actions/audio-device.actions';

@Component({
  selector: 'mwo-player',
  templateUrl: './player.component.html',
  styleUrls: ['./player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlayerComponent implements OnInit {
  @Input() playerClass = 'full';
  @Output() onDisplayMiniPlayer: EventEmitter<null> = new EventEmitter<null>();
  @Select(['player', 'media']) currentMedia$: Observable<Media>;
  @Select(['player', 'playerContextType']) context$: Observable<string>;
  @Select(['player', 'isPlaying']) isPlaying$: Observable<boolean>;
  @Select(['player', 'waitList']) waitList$: Observable<Media[]>;
  @Select$(['wishlist', 'assoMediaEvent'], extractPropertyFromArray('media')) wishlist$: Observable<Media[]>;
  @Select$('blacklist', extractPropertyFromArray('media')) blacklist$: Observable<Media[]>;
  @Select$(['audio_devices'], toArray) audioDevices$: Observable<[any]>;

  // @ts-ignore
  isInBlacklist$: Observable<boolean> = isItemInArray(this.currentMedia$, this.blacklist$, 'id');
  // @ts-ignore
  isInWishlist$: Observable<boolean> = isItemInArray(this.currentMedia$, this.wishlist$, 'id');
  playlists$: Observable<{ [key: string]: AssoMediaEvent }>;
  coverUrl = 'assets/icons/default_icon.webp';
  artist: string;
  songTitle: string;
  duration: number;
  displayVolume = false;
  isSpotify: boolean;
  showHearts = false;
  gifFinished = true;
  isDesktop = false;
  isTitleOverflowing = false;
  isArtistOverflowing = false;
  isSoundMessage = false;
  isSourcesDisabled = true;
  isPlaybackActivated = false;
  protected readonly environment = environment;

  constructor(private ngRedux: NgRedux<PlaylistRemoteAppState>,
    private router: Router,
    private playerService: PlayerService,
    private syncDialogService: SyncDialogService,
    private playbackService: PlaybackService,
    public dialog: MatDialog) {
  }

  get progress$() {
    return this.playerService.progress$;
  }

  get timer$() {
    return this.playerService.timer$;
  }

  get volume$() {
    return this.playerService.volume$;
  }

  ngOnInit() {
    this.ngRedux.dispatch(AGetAudioDevices());
    this.audioDevices$.subscribe((data) => {
      if (data[0].length > 1) {
        this.isSourcesDisabled = false;
      }
    });
    this.currentMedia$.subscribe(res => {
      if (res && res.id) {
        this.isSpotify = res.id && res.id.startsWith('SPOTIFY');
        this.songTitle = res.title;
        if (res.artist) {
          if (typeof res.artist === 'string') {
            this.artist = res.artist;
          } else {
            this.artist = res.artist.name;
          }
        }
        this.isTitleOverflowing = this.songTitle?.length > 21;
        this.isArtistOverflowing = this.artist?.length > 20;
        if (res.album && res.album.artwork) {
          this.coverUrl = res.album.artwork;
        } else {
          this.coverUrl = `${environment.remoteApi}/${environment.apiVersion}/media/artwork/` + res.id;
        }
        if (res.duration) {
          this.isSoundMessage = res.duration < 60;
          this.duration = res.duration;
        }
      }
    });
    this.isDesktop = environment.enableDesktopMode;
  }

  play() {
    this.ngRedux.dispatch(APlay());
    this.syncDialogService.openSyncDialog('PLAY');
  }

  pause() {
    this.ngRedux.dispatch(APause());
    this.syncDialogService.openSyncDialog('PAUSE');
  }

  next() {
    this.ngRedux.dispatch(ANext());
    // this.ngRedux.dispatch(ASetMessage('Next song'));
    setTimeout(() => {
      this.syncDialogService.openSyncDialog('NEXT');
    }, 300);
  }

  navToContext(context) {
    this.router.navigate([context]);
  }

  volumeDisplay() {
    this.dialog.closeAll();
    this.dialog.open(VolumeDialogComponent, {
      disableClose: false, data: this.volume$, minHeight: 440, maxWidth: 300, maxHeight: 440, panelClass: 'volume-modal'
    });
    this.displayVolume = true;
    document.body.classList.add('cdk-global-scrollblock');
    this.dialog.afterAllClosed.subscribe(res => {
      document.body.classList.remove('cdk-global-scrollblock');
      this.displayVolume = false;
    });
  }

  playbackDisplay() {
    this.isPlaybackActivated = true;
    this.playbackService.openPlaybackDialog();
    this.dialog.afterAllClosed.subscribe(res => {
      this.isPlaybackActivated = false;
    });
  }

  like(media: Media) {
    //console.log('------------------------LOG like', media);
    this.ngRedux.dispatch(ALike({ media }));
    this.ngRedux.dispatch(AUnblacklist(media.id));
    // this.ngRedux.dispatch(ASetMessage('LIKE ' + media.title));

    if (!environment.enableDesktopMode) {
      this.syncDialogService.openSyncDialog();
    }

    this.showHearts = true;
    this.gifFinished = false;

    setTimeout(() => {
      this.showHearts = false;
      this.gifFinished = true;
    }, 2000);
  }

  unlike(media: Media) {
    //console.log('------------------UNLIKE');
    this.ngRedux.dispatch(ADislike(media.id));
    if (!environment.enableDesktopMode) {
      this.syncDialogService.openSyncDialog();
    }
  }

  blacklist(media: Media) {
    //console.log('------------------blacklist');
    this.ngRedux.dispatch(ABlacklist({ id: media.id, media, blacklistDate: new Date().getTime() }));
    this.ngRedux.dispatch(ADislike(media.id));
    if (!environment.enableDesktopMode) {
      this.syncDialogService.openSyncDialog();
    }
  }

  unBlacklist(media: Media) {
    this.ngRedux.dispatch(AUnblacklist(media.id));
    if (!environment.enableDesktopMode) {
      this.syncDialogService.openSyncDialog();
    }
  }

  setVolume($event) {
    this.ngRedux.dispatch(ASlideVolume($event));
  }

  openSonos(): void {
    const dialogRef = this.dialog.open(SonosComponent, {
      data: {}, width: '250px', position: {
        bottom: '45px', right: '95px'
      }
    });
  }

}
